import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { useOutletContext, useParams } from 'react-router-dom';
import axiosInstant from '../../../api';
import {
  changeBrandTeamAdmin,
  postCreateAutoLoginRedirect,
  getUserToken,
  adminRmnBrandCategoriesEndpoint,
} from '../../../api/endpoints';
import { useBoundStore } from '../../../store';
import { useRemoveBrandTeam } from '../hooks/userRemoveBrandTeam';
import { BrandTeamMember } from './BrandTeamMember';
import { BrandTeamMemberData } from './data/type';
import { VerificationDialog } from './verificationDialog';
import { TeamMemberModal } from './TeamMemberModal';
import { brandIsNetwork } from '../../../utils/plans';
import { EmptyPanel } from '../projects/common/EmptyPanel';
import { IconUser } from '../../../utils/icons';
import { useUser } from '../../../hooks/useUser';
import { IBrandDetailsContext } from '../SingleBrandDetails';
import { flattenRmnTreePaths, PickerOption } from './RmnPermissionPickers';

export function BrandTeamMembers() {
  const { id } = useParams();
  const { brand, brandTeam, getBrandTeam } = useOutletContext<IBrandDetailsContext>();
  const brandId = id ? parseInt(id, 10) : 0;
  const isRmnBrand = !!(brand && brand.rmn);

  // RMN networks: the inline permission pickers need the network's
  // supplier brands (already on the brand detail) and its category tree
  // (admin endpoint, fetched once per page).
  const brandOptions = useMemo<PickerOption[]>(
    () =>
      isRmnBrand
        ? ((brand && brand.associated_brands) || []).map((b: { id: number; name: string }) => ({
            id: b.id,
            label: b.name,
          }))
        : [],
    [brand, isRmnBrand],
  );
  const [categoryOptions, setCategoryOptions] = useState<PickerOption[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (isRmnBrand && brandId) {
      axiosInstant
        .get(`${adminRmnBrandCategoriesEndpoint(brandId)}?tree=1`)
        .then(({ data }) => {
          if (!cancelled) setCategoryOptions(flattenRmnTreePaths(Array.isArray(data) ? data : []));
        })
        .catch(() => {
          if (!cancelled) setCategoryOptions([]);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [isRmnBrand, brandId]);

  // Server-confirmed permission patches, keyed by user — keeps chips in
  // sync after a save without refetching the whole team. Reset whenever
  // the parent's team list reloads.
  const [permissionPatches, setPermissionPatches] = useState<
    Record<number, Partial<BrandTeamMemberData>>
  >({});
  useEffect(() => {
    setPermissionPatches({});
  }, [brandTeam]);
  const onPermissionsSaved = (userid: number, patch: Partial<BrandTeamMemberData>) =>
    setPermissionPatches((prev) => ({ ...prev, [userid]: { ...(prev[userid] || {}), ...patch } }));
  const [pendingUser, setPendingUser] = useState<any>(null);
  const [pendingAdmin, setPendingAdmin] = useState<any>(null);
  const { removeFromBrandTeam, loading: removing } =
    useRemoveBrandTeam(brandId);
  const updateUserInfo = useBoundStore((state) => state.updateUserInfo);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const autoLogin = useBoundStore((state) => state.autoLogin);
  const logout = useBoundStore((state) => state.logout);
  const { role } = useUser();
  

  const handleAddMember = () => {
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleEditMember = (user: any) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const removeTeamMember = async (userData: any) => {
    setPendingUser(userData);
  };

  const changeAdminTeamMember = async (userData: any) => {
    setPendingAdmin(userData);
  };

  const changeBrandAdminAction = async () => {
    if (!pendingAdmin) return;
    try {
      await axiosInstant.post(
        changeBrandTeamAdmin(
          brandId.toString(),
          pendingAdmin.userid.toString()
        ),
        {}
      );
      await updateUserInfo();
      setPendingAdmin(null);
      toast.success('User has been made the brand\'s admin.');
      getBrandTeam();
    } catch (error) {
      toast.error((error as AxiosError).message);
    }
  };

  const removeFromBrandAction = async () => {
    if (!pendingUser) return;
    try {
      await removeFromBrandTeam(pendingUser.userid.toString());
      setPendingUser(null);
      toast.success('User has been removed from brand team.');
      getBrandTeam();
    } catch (error) {
      toast.error((error as AxiosError).message);
    }
  };

  const createAutoLoginUrl = async (user: any) => {
    try {
      const res = await axiosInstant.post(postCreateAutoLoginRedirect(), {
        userid: user.userid.toString(),
      });

      const { redirectUrl } = res?.data || {};
      // copy to clipboard
      navigator.clipboard.writeText(redirectUrl);
      toast.success('Auto login URL copied to clipboard');
    } catch (error) {
      toast.error((error as AxiosError).message);
    }
  };

  const loginAsUser = async (user: any) => {
    try {
      const tokens = await axiosInstant.get(getUserToken(user.userid.toString()));
      await autoLogin({ tokens: tokens.data });
      window.location.href = '/';
    } catch (error) {
      toast.error((error as AxiosError).message);
    }
  };

  const sortedMembers = useMemo(
    () =>
      ((brandTeam || []) as BrandTeamMemberData[])
        .map((m) => (permissionPatches[m.userid] ? { ...m, ...permissionPatches[m.userid] } : m))
        .sort((a, b) => a.usertype.localeCompare(b.usertype)) as Array<BrandTeamMemberData>,
    [brandTeam, permissionPatches]
  );

  const hasTeamMembers =
    sortedMembers.filter((member) => member != null).length > 0;
  return (
    <div className="flex flex-col w-full justify-between items-center bg-surface rounded-xl p-6 max-w-[768px] mx-auto">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={2}
        width="100%"
      >
        <Typography variant="h4" className="font-[CircularStd] text-5xl mr-1">
          Team Members
        </Typography>
        {hasTeamMembers && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddMember}
          >
            New User
          </Button>
        )}
      </Box>
      {!hasTeamMembers && (
        <EmptyPanel
          text="Invite team members to share access."
          showSpacing
          icon={
            <div className="w-[50px] h-[50px] flex items-center justify-center rounded-full !border !border-accent">
              <IconUser width={28} height={28} />
            </div>
          }
          button={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddMember}
            >
              New User
            </Button>
          }
        />
      )}
      {sortedMembers.map((member, index) => (
        <BrandTeamMember
          key={index}
          data={member}
          onDelete={removeTeamMember}
          onChangeAdmin={changeAdminTeamMember}
          onEdit={() => handleEditMember(member)}
          createAutoLoginUrl={createAutoLoginUrl}
          loginAsUser={loginAsUser}
          role={role}
          rmn={isRmnBrand}
          brandId={brandId}
          brandOptions={brandOptions}
          categoryOptions={categoryOptions}
          onPermissionsSaved={onPermissionsSaved}
        />
      ))}
      {pendingUser && (
        <VerificationDialog
          title="Remove this user"
          onOkay={removeFromBrandAction}
          isLoading={removing}
          onClose={() => setPendingUser(null)}
          body={
            <>
              Are you sure you want to remove{' '}
              <strong>{pendingUser.displayName}</strong> from your team?
            </>
          }
        />
      )}
      {pendingAdmin && (
        <VerificationDialog
          title="Change admin user"
          onOkay={changeBrandAdminAction}
          isLoading={removing}
          onClose={() => setPendingAdmin(null)}
          body={
            <>
              Are you sure you want to make{' '}
              <strong>{pendingAdmin.displayName}</strong> the admin user for
              your team?
            </>
          }
        />
      )}
      <TeamMemberModal
        open={isModalOpen}
        onClose={handleCloseModal}
        user={selectedUser}
        brandId={brandId}
        brandHasAdmin={(brandTeam || []).some((m) => m.usertype === 'brand admin')}
        networkBrand={brandIsNetwork(brand)}
        onSuccess={getBrandTeam}
      />
    </div>
  );
}
